import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFilter } from 'react-icons/fa';
import UiSelect from '../components/UiSelect.jsx';
import { getAllStudentsData } from '../services/api.js';
import API from '../api/index.js';
import { useToast } from '../context/ToastContext.jsx';
import { useUrlFilters } from '../hooks/useUrlFilters.js';
import '../styles/pages/paymentplans.css';

const STANDARD_PLANS = [
  { label: 'Monthly', value: 'Monthly', days: 30 },
  { label: 'Quarterly', value: 'Quarterly', days: 90 },
  { label: 'Half Yearly', value: 'Half Yearly', days: 180 },
  { label: 'Yearly', value: 'Yearly', days: 365 }
];

const EMPTY_PLAN = {
  plan_name: '',
  seat_category: 'ac',
  duration_days: '',
  amount: '',
  library_id: 'global'
};

const PLAN_CATEGORIES = [
  { id: 'ac', label: 'AC', icon: <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2v20M4.93 4.93l14.14 14.14M2 12h20M4.93 19.07 19.07 4.93M8 4l1.5 2.6M16 20l-1.5-2.6M4 8l2.6 1.5M20 16l-2.6-1.5M4 16l2.6-1.5M20 8l-2.6 1.5M8 20l1.5-2.6M16 4l-1.5 2.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> },
  { id: 'non_ac', label: 'Non-AC', icon: <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3v18m-6-3 12-12M6 6l12 12M4 12h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg> },
  { id: 'reserved', label: 'Reserved', icon: <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.7"/><path d="M8 10V7a4 4 0 1 1 8 0v3m-4 5v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg> }
];

const getPlanId = plan => plan.plan_id || plan.planid || plan.id || plan._id;
const getLibraryId = library => library.library_id || library.id || library._id;
const getPlanDuration = plan => Number(plan.duration_days || plan.durationdays || plan.duration || 0);

function getDurationLabel(days) {
  const standardPlan = STANDARD_PLANS.find(plan => plan.days === days);
  return standardPlan ? `${standardPlan.label} · ${days} days` : days ? `${days} days` : 'Duration not set';
}

function normalizeCategory(plan) {
  const category = String(plan.seat_category || plan.seatcategory || plan.category || 'ac').toLowerCase().replace(/[^a-z]/g, '');
  if (category.includes('reserved')) return 'reserved';
  if (category.includes('non')) return 'non_ac';
  return 'ac';
}

function getTenantHeaders() {
  try {
    const tenant = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
    const id = tenant.id || tenant.tenantId;
    return id ? { 'X-Tenant-ID': String(id) } : {};
  } catch (error) {
    console.error('Failed to read tenant information for payment plans:', error);
    return {};
  }
}

function PaymentPlansSkeleton({ onAddPlan }) {
  return (
    <div className="plans-page plans-skeleton" aria-label="Loading payment plans" aria-busy="true">
      <header className="plans-header plans-page-header dash-header">
        <div className="plans-heading-row dash-header-bar">
          <div className="plans-header-left">
            <span className="plans-heading-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v18H6.5A2.5 2.5 0 0 1 4 18.5v-13ZM4 18.5A2.5 2.5 0 0 1 6.5 16H20M8 7h8m-8 4h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
            <div className="plans-header-info">
              <div className="plans-heading-text"><h1 className="dash-header-title">Payment Plans</h1><p className="dash-header-subtitle">Manage subscription plans and pricing</p></div>
              <div className="dash-header-actions plans-header-actions"><button type="button" className="add-payment-btn plans-header-add dash-action-btn primary" onClick={onAddPlan}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>Add Plan</button></div>
            </div>
          </div>
        </div>
      </header>
      <div className="plans-skeleton-stats">
        {[1, 2, 3, 4].map(item => <div className="payment-stat" key={item}><span className="plans-skeleton-block" /><b className="plans-skeleton-block" /><small className="plans-skeleton-block" /></div>)}
      </div>
      <div className="plans-filter-collapsed-control">
        <span className="plans-skeleton-block plans-skeleton-toggle" />
      </div>
      <div className="plans-skeleton-table">
        <div className="plans-skeleton-table-heading plans-skeleton-block" />
        {Array.from({ length: 8 }, (_, index) => <div className="plans-skeleton-table-row" key={index}>
          <span className="plans-skeleton-block" />
          <span className="plans-skeleton-block" />
          <span className="plans-skeleton-block" />
          <span className="plans-skeleton-block" />
          <span className="plans-skeleton-block" />
        </div>)}
      </div>
    </div>
  );
}

export function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PLAN });
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterLibrary, setFilterLibrary] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const { showToast, showConfirmationToast } = useToast();

  useUrlFilters([
    { key: 'library', value: filterLibrary, setValue: setFilterLibrary, defaultValue: 'all' },
    { key: 'category', value: filterCategory, setValue: setFilterCategory, defaultValue: 'all' },
  ]);

  const fetchAll = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const headers = getTenantHeaders();
    const requestConfig = Object.keys(headers).length ? { headers } : undefined;
    try {
      const [plansResponse, librariesResponse] = await Promise.all([
        API.get('/api/payment-plans', requestConfig),
        API.get('/api/libraries', requestConfig)
      ]);
      const plansData = plansResponse.data;
      const librariesData = librariesResponse.data;
      const plansList = Array.isArray(plansData) ? plansData : plansData?.data || plansData?.plans || plansData?.payment_plans || plansData?.paymentPlans || [];
      const librariesList = Array.isArray(librariesData) ? librariesData : librariesData?.data || librariesData?.libraries || [];
      if (!Array.isArray(plansList) || !Array.isArray(librariesList)) {
        throw new Error('Payment plan or library response was not a list.');
      }
      setPlans(plansList);
      setLibraries(librariesList);
    } catch (error) {
      console.error('Failed to load payment plans and libraries:', error);
      showToast(error.response?.data?.error || 'Failed to load payment plans.', 'error');
    }

    try {
      const studentsResponse = await getAllStudentsData(null, null, true);
      const studentList = Array.isArray(studentsResponse)
        ? studentsResponse
        : studentsResponse?.students || studentsResponse?.data || [];
      if (!Array.isArray(studentList)) throw new Error('Student response was not a list.');
      setStudents(studentList);
    } catch (error) {
      console.error('Failed to load enrolled student counts for payment plans:', error);
      showToast('Payment plans loaded, but enrollment counts could not be refreshed.', 'error');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const libraryNames = useMemo(() => {
    const map = new Map();
    libraries.forEach(library => {
      const id = getLibraryId(library);
      if (id !== undefined && id !== null) map.set(String(id), library.library_name || library.name);
    });
    return map;
  }, [libraries]);

  const enrichedPlans = useMemo(() => plans.map(plan => ({
    ...plan,
    _id: getPlanId(plan),
    _category: normalizeCategory(plan),
    _libraryName: plan.library_name || plan.library?.library_name || plan.library?.name || libraryNames.get(String(plan.library_id ?? plan.library?.library_id ?? plan.library?.id)) || null,
    _isGlobal: (plan.library_id ?? plan.library?.library_id ?? plan.library?.id) == null || plan.library_id === 'global'
  })), [plans, libraryNames]);

  const filteredPlans = useMemo(() => enrichedPlans.filter(plan => {
    if (filterLibrary === 'global' && !plan._isGlobal) return false;
    if (filterLibrary !== 'all' && filterLibrary !== 'global' && String(plan.library_id ?? plan.library?.library_id ?? plan.library?.id) !== String(filterLibrary)) return false;
    return filterCategory === 'all' || plan._category === filterCategory;
  }), [enrichedPlans, filterLibrary, filterCategory]);

  const enrolledMap = useMemo(() => {
    const map = new Map();
    students.forEach(student => {
      const planId = student.plan_id || student.planId || student.payment_plan_id || student.planid || student.plan?.plan_id || student.plan?.planid || student.payment_plan?.plan_id || student.payment_plan?.planid;
      if (planId != null) map.set(String(planId), (map.get(String(planId)) || 0) + 1);
    });
    return map;
  }, [students]);

  const categoryCounts = useMemo(() => enrichedPlans.reduce((counts, plan) => {
    counts[plan._category] += 1;
    return counts;
  }, { ac: 0, non_ac: 0, reserved: 0 }), [enrichedPlans]);

  const openCreate = () => {
    setForm({ ...EMPTY_PLAN });
    setEditing('new');
  };

  const openEdit = plan => {
    setForm({
      plan_name: plan.plan_name || plan.planname || plan.name || '',
      seat_category: plan.seat_category || plan.seatcategory || 'ac',
      duration_days: String(plan.duration_days || plan.durationdays || ''),
      amount: String(plan.amount ?? plan.price ?? ''),
      library_id: (plan.library_id ?? plan.library?.library_id ?? plan.library?.id) == null || plan.library_id === 'global'
        ? 'global'
        : String(plan.library_id ?? plan.library?.library_id ?? plan.library?.id)
    });
    setEditing(plan);
  };

  const savePlan = async event => {
    event.preventDefault();
    const durationDays = Number(form.duration_days);
    const amount = Number(form.amount);
    if (!form.plan_name || !Number.isInteger(durationDays) || durationDays < 1 || !Number.isFinite(amount) || amount <= 0) {
      showToast('Enter a plan name, a duration of at least one day, and a valid amount.', 'error');
      return;
    }

    setSaving(true);
    const headers = getTenantHeaders();
    const requestConfig = Object.keys(headers).length ? { headers } : undefined;
    const libraryId = form.library_id === 'global' ? null : form.library_id;
    try {
      if (editing === 'new') {
        await API.post('/api/payment-plans', {
          planname: form.plan_name,
          seatcategory: form.seat_category,
          durationdays: durationDays,
          amount,
          library_id: libraryId
        }, requestConfig);
        showToast('Plan created successfully.', 'success');
      } else {
        const id = getPlanId(editing);
        await API.put(`/api/payment-plans/${id}`, {
          plan_name: form.plan_name,
          seat_category: form.seat_category,
          duration_days: durationDays,
          amount,
          library_id: libraryId
        }, requestConfig);
        showToast('Plan updated successfully.', 'success');
      }
      setEditing(null);
      setForm({ ...EMPTY_PLAN });
      await fetchAll(false);
    } catch (error) {
      console.error('Failed to save payment plan:', error);
      showToast(error.response?.data?.error || error.response?.data?.message || 'Failed to save payment plan.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async plan => {
    const id = getPlanId(plan);
    setDeletingId(id);
    try {
      const headers = getTenantHeaders();
      await API.delete(`/api/payment-plans/${id}`, Object.keys(headers).length ? { headers } : undefined);
      setPlans(current => current.filter(item => String(getPlanId(item)) !== String(id)));
      showToast('Plan deleted successfully.', 'success');
    } catch (error) {
      console.error('Failed to delete payment plan:', error);
      showToast(error.response?.data?.error || 'Failed to delete payment plan.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const requestDelete = plan => {
    showConfirmationToast(
      `Delete ${plan.plan_name || plan.planname || plan.name || 'this plan'}? This action cannot be undone.`,
      () => deletePlan(plan)
    );
  };

  const toggleStatus = async plan => {
    const id = getPlanId(plan);
    const previousStatus = String(plan.status || 'active').toLowerCase();
    const nextStatus = previousStatus === 'active' ? 'inactive' : 'active';
    setTogglingId(id);
    setPlans(current => current.map(item => String(getPlanId(item)) === String(id) ? { ...item, status: nextStatus } : item));
    try {
      const headers = getTenantHeaders();
      await API.put(`/api/payment-plans/${id}`, {
        plan_name: plan.plan_name || plan.planname || plan.name || '',
        seat_category: plan.seat_category || plan.seatcategory || plan.category || 'ac',
        duration_days: Number(plan.duration_days || plan.durationdays || plan.duration || 0),
        amount: Number(plan.amount ?? plan.price ?? 0),
        library_id: plan.library_id ?? plan.library?.library_id ?? plan.library?.id ?? null,
        status: nextStatus
      }, Object.keys(headers).length ? { headers } : undefined);
      showToast(nextStatus === 'active' ? 'Plan activated.' : 'Plan deactivated.', 'success');
    } catch (error) {
      console.error('Failed to update payment plan status:', error);
      setPlans(current => current.map(item => String(getPlanId(item)) === String(id) ? { ...item, status: previousStatus } : item));
      showToast(error.response?.data?.error || 'Failed to update payment plan status.', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const clearFilters = () => {
    setFilterLibrary('all');
    setFilterCategory('all');
  };

  if (loading) return <PaymentPlansSkeleton onAddPlan={openCreate} />;

  return (
    <div className="plans-page">
      <header className="plans-header plans-page-header dash-header">
        <div className="plans-heading-row dash-header-bar">
          <div className="plans-header-left">
            <span className="plans-heading-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v18H6.5A2.5 2.5 0 0 1 4 18.5v-13ZM4 18.5A2.5 2.5 0 0 1 6.5 16H20M8 7h8m-8 4h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
            <div className="plans-header-info">
              <div className="plans-heading-text"><h1 className="dash-header-title">Payment Plans</h1><p className="dash-header-subtitle">Manage subscription plans and pricing</p></div>
              <div className="dash-header-actions plans-header-actions"><button type="button" className="add-payment-btn plans-header-add dash-action-btn primary" onClick={openCreate}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>Add Plan</button></div>
            </div>
          </div>
        </div>
      </header>
      <div className="payment-stats">
        <div className="payment-stat"><div className="payment-stat-label"><span>Total Plans</span><i className="payment-stat-icon violet"><svg viewBox="0 0 24 24" fill="none"><path d="M6 3h9l4 4v14H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm8 1v4h4m-9 4h6m-6 4h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg></i></div><b>{plans.length}</b><small>Across all libraries</small></div>
        <div className="payment-stat"><div className="payment-stat-label"><span>AC Plans</span><i className="payment-stat-icon blue">{PLAN_CATEGORIES[0].icon}</i></div><b>{categoryCounts.ac}</b><small>Air-conditioned seats</small></div>
        <div className="payment-stat"><div className="payment-stat-label"><span>Non-AC Plans</span><i className="payment-stat-icon green">{PLAN_CATEGORIES[1].icon}</i></div><b>{categoryCounts.non_ac}</b><small>Standard seats</small></div>
        <div className="payment-stat"><div className="payment-stat-label"><span>Reserved Plans</span><i className="payment-stat-icon amber">{PLAN_CATEGORIES[2].icon}</i></div><b>{categoryCounts.reserved}</b><small>Reservation fees</small></div>
      </div>

      {showFilters ? (
        <section className="filter-card plans-filter-card" aria-label="Payment plan filters">
          <div className="plans-filter-heading">
            <div><h2>Filter payment plans</h2><p>Choose a category or library to narrow the results.</p></div>
            <div className="plans-filter-heading-actions">
              <button type="button" className="clear-btn" onClick={clearFilters} disabled={filterLibrary === 'all' && filterCategory === 'all'}>Clear filters</button>
              <button type="button" className="filter-toggle-btn" aria-expanded="true" aria-controls="plans-filter-body" onClick={() => setShowFilters(false)}>
                <FaFilter aria-hidden="true" /><span>Hide filters</span>
              </button>
            </div>
          </div>
          <div className="plans-filter-body" id="plans-filter-body">
            <div className="quick-filters">
              <div className="filter-chips" aria-label="Filter by seat category">
                {[{ label: 'All', value: 'all' }, ...PLAN_CATEGORIES.map(category => ({ label: category.label, value: category.id }))].map(category => (
                  <button type="button" key={category.value} onClick={() => setFilterCategory(category.value)} className={`chip ${filterCategory === category.value ? 'active' : ''}`} aria-pressed={filterCategory === category.value}>
                    {category.value !== 'all' && <span className="category-chip-icon">{PLAN_CATEGORIES.find(item => item.id === category.value)?.icon}</span>}{category.label}
                  </button>
                ))}
              </div>
              <div className="plans-filter-actions">
                <UiSelect className="library-filter" value={filterLibrary} onChange={event => setFilterLibrary(event.target.value)} aria-label="Filter by library">
                  <option value="all">All Libraries</option>
                  <option value="global">Global Plans</option>
                  {libraries.map(library => <option key={getLibraryId(library)} value={getLibraryId(library)}>{library.library_name || library.name}</option>)}
                </UiSelect>
              </div>
            </div>
            <div className="filter-footer">
              <span>Showing <strong>{filteredPlans.length}</strong> of <strong>{plans.length}</strong> plans</span>
              <span>Library: <strong>{filterLibrary === 'all' ? 'All libraries' : filterLibrary === 'global' ? 'Global plans' : libraryNames.get(String(filterLibrary)) || 'Selected library'}</strong></span>
            </div>
          </div>
        </section>
      ) : (
        <div className="filter-toggle-row">
          <button type="button" className="filter-toggle-btn" aria-expanded="false" aria-controls="plans-filter-body" onClick={() => setShowFilters(true)}>
            <FaFilter aria-hidden="true" /><span>Show filters</span>
          </button>
        </div>
      )}

      {filteredPlans.length === 0 ? (
        <section className="plans-empty-state">
          <span className="plans-empty-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v18H6.5A2.5 2.5 0 0 1 4 18.5v-13ZM4 18.5A2.5 2.5 0 0 1 6.5 16H20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
          <h2>No payment plans found</h2>
          <p>{plans.length ? 'Try changing the filters or clear them to see all plans.' : 'Create your first payment plan to get started.'}</p>
          {plans.length ? <button type="button" className="clear-btn" onClick={clearFilters}>Clear filters</button> : <button type="button" className="add-payment-btn" onClick={openCreate}>Add Plan</button>}
        </section>
      ) : (
        <div className="plan-category-groups">
          {(filterCategory === 'all' ? PLAN_CATEGORIES : PLAN_CATEGORIES.filter(category => category.id === filterCategory)).map(category => {
            const categoryPlans = filteredPlans
              .filter(plan => plan._category === category.id)
              .sort((planA, planB) => getPlanDuration(planA) - getPlanDuration(planB)
                || String(planA._libraryName || '').localeCompare(String(planB._libraryName || ''))
                || String(planA.plan_name || planA.planname || '').localeCompare(String(planB.plan_name || planB.planname || '')));
            const libraryCount = new Set(categoryPlans.map(plan => plan._libraryName).filter(Boolean)).size;
            const includesGlobalPlans = categoryPlans.some(plan => plan._isGlobal);
            return (
              <section className="plan-category-section" key={category.id}>
                <header className="plan-category-heading">
                  <span className={`plan-icon category-${category.id}`}>{category.icon}</span>
                  <div><h2>{category.id === 'reserved' ? 'Reserved Plans' : `${category.label} Seat Plans`}</h2><p>{categoryPlans.length} {categoryPlans.length === 1 ? 'plan' : 'plans'} · {includesGlobalPlans ? 'includes global plans' : `${libraryCount} ${libraryCount === 1 ? 'library' : 'libraries'}`}</p></div>
                </header>
                {categoryPlans.length ? <div className="plans-table-scroll">
                  <table className="plans-table">
                    <thead><tr><th>Plan</th><th>Library</th><th>Duration</th><th>Amount</th><th>Enrolled</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                      {categoryPlans.map((plan, index) => {
                        const id = plan._id;
                        const active = String(plan.status || 'active').toLowerCase() === 'active';
                        const enrolled = enrolledMap.get(String(id)) || Number(plan.enrolled || 0);
                        const duration = getPlanDuration(plan);
                        const previousDuration = index > 0 ? getPlanDuration(categoryPlans[index - 1]) : null;
                        return <React.Fragment key={id}>
                          {duration !== previousDuration && <tr className="plans-duration-divider"><th colSpan="7">{getDurationLabel(duration)}</th></tr>}
                          <tr className={active ? '' : 'plans-row-inactive'}>
                            <td><div className="plans-table-name"><span className={`plans-table-category-icon category-${category.id}`}>{category.icon}</span><div><strong>{plan.plan_name || plan.planname || plan.name || 'Payment Plan'}</strong><small>{category.id === 'reserved' ? 'Reservation fee' : `${category.label} seat plan`}</small></div></div></td>
                            <td><span className={`plans-library-tag ${plan._isGlobal ? 'global' : ''}`}>{plan._isGlobal ? 'Global' : plan._libraryName || 'Unknown library'}</span></td>
                            <td>{duration ? `${duration} days` : 'Not set'}</td>
                            <td className="plans-table-amount">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(plan.amount ?? plan.price) || 0)}</td>
                            <td>{enrolled} {enrolled === 1 ? 'student' : 'students'}</td>
                            <td><span className={`plans-table-status ${active ? 'active' : 'inactive'}`}>{active ? 'Active' : 'Inactive'}</span></td>
                            <td><div className="plans-table-actions">
                              <button type="button" onClick={() => openEdit(plan)}>Edit</button>
                              <button type="button" onClick={() => toggleStatus(plan)} disabled={String(togglingId) === String(id)}>{String(togglingId) === String(id) ? 'Saving…' : active ? 'Deactivate' : 'Activate'}</button>
                              <button type="button" className="delete" onClick={() => requestDelete(plan)} disabled={String(deletingId) === String(id)}>{String(deletingId) === String(id) ? 'Deleting…' : 'Delete'}</button>
                            </div></td>
                          </tr>
                        </React.Fragment>;
                      })}
                    </tbody>
                  </table>
                </div> : <div className="category-empty">No {category.id === 'reserved' ? 'reserved' : category.label} plans for the selected filters.</div>}
              </section>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onMouseDown={event => event.target === event.currentTarget && !saving && setEditing(null)}>
          <section className="modal plan-editor">
            <div className="modal-head">
              <div><span className="eyebrow">PAYMENT PLANS</span><h2>{editing === 'new' ? 'Create Payment Plan' : 'Edit Payment Plan'}</h2></div>
              <button type="button" className="close-button" aria-label="Close payment plan form" onClick={() => !saving && setEditing(null)}>×</button>
            </div>
            <form onSubmit={savePlan}>
              <div className="form-grid">
                <label>Plan Name <em>*</em><UiSelect required value={form.plan_name} onChange={event => {
                  const selected = STANDARD_PLANS.find(plan => plan.value === event.target.value);
                  setForm(current => ({ ...current, plan_name: event.target.value, duration_days: selected ? String(selected.days) : current.duration_days }));
                }}>
                  <option value="">Select Plan Name</option>
                  {form.plan_name && !STANDARD_PLANS.some(plan => plan.value === form.plan_name) && <option value={form.plan_name}>{form.plan_name}</option>}
                  {STANDARD_PLANS.map(plan => <option key={plan.value} value={plan.value}>{plan.label}</option>)}
                </UiSelect></label>
                <label>Library <em>*</em><UiSelect required value={form.library_id} onChange={event => setForm(current => ({ ...current, library_id: event.target.value }))}>
                  <option value="global">Global (All Libraries)</option>
                  {libraries.map(library => <option key={getLibraryId(library)} value={getLibraryId(library)}>{library.library_name || library.name}</option>)}
                </UiSelect></label>
                <label>Seat Category <em>*</em><UiSelect required value={form.seat_category} onChange={event => setForm(current => ({ ...current, seat_category: event.target.value }))}>
                  <option value="ac">AC</option><option value="non_ac">Non-AC</option><option value="reserved">Reservation Fee</option>
                </UiSelect></label>
                <label>Duration (Days) <em>*</em><input type="number" min="1" step="1" required value={form.duration_days} onChange={event => setForm(current => ({ ...current, duration_days: event.target.value }))} placeholder="e.g. 30" /><small>Automatically filled for standard plans; you can adjust it.</small></label>
                <label className="plan-amount-field">Amount (₹) <em>*</em><input type="number" min="0.01" step="0.01" required value={form.amount} onChange={event => setForm(current => ({ ...current, amount: event.target.value }))} placeholder="e.g. 5000" /></label>
              </div>
              <div className="modal-actions"><button type="button" className="button ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</button><button type="submit" className="button primary" disabled={saving}>{saving ? 'Saving...' : editing === 'new' ? 'Create Plan' : 'Update Plan'}</button></div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
